import { cva } from 'class-variance-authority'

export const itemVariants = cva(
  `
    flex w-full appearance-none items-center bg-transparent text-left font-body text-content-base-main no-underline
    transition-colors motion-color
  `,
  {
    variants: {
      variant: {
        default: '',
        outline: 'rounded-20 border border-line-base-basic bg-surface-base-normal',
        muted: 'rounded-20 bg-surface-base-hover',
      },
      size: {
        default: 'gap-50 px-50 py-50',
        sm: 'gap-30 px-40 py-30',
      },
      // An interactive row owns its focus indicator end to end: it suppresses the native outline and
      // draws its own. A non-interactive row keeps the native one, so a row made focusable for some
      // other reason (anchor target, tooltip trigger, programmatic focus) is never left with none.
      // The indicator is an inset outline rather than a ring because `Item.Group variant="outline"`
      // clips box-shadows with `overflow-hidden`, and forced-colors mode drops them entirely.
      // `outline-solid` is load-bearing: `outline-3` only sets `outline-style: var(--tw-outline-style)`,
      // which `outline-none` above pins to `none`, and tailwind-merge drops a bare `outline` as a
      // conflict with `outline-3`.
      interactive: {
        true: `
          cursor-pointer outline-none
          hover:bg-surface-base-hover
          focus-visible:outline-3 focus-visible:-outline-offset-3 focus-visible:outline-line-base-input-focus/50
          focus-visible:outline-solid
        `,
        false: '',
      },
    },
    defaultVariants: { variant: 'default', size: 'default', interactive: false },
  },
)

export const itemTitleVariants = cva('text-20 leading-30 text-content-base-main', {
  variants: {
    overflow: {
      truncate: 'truncate',
      wrap: '',
    },
    weight: {
      regular: 'font-400',
      medium: 'font-500',
      semibold: 'font-600',
    },
  },
  defaultVariants: { overflow: 'truncate', weight: 'semibold' },
})

export const itemGroupVariants = cva('flex flex-col', {
  variants: {
    variant: {
      default: 'gap-20',
      // Separates rows without drawing a container: for a group already sitting on an enclosed
      // surface (dialog body, card), where an `outline` border would draw a double frame. The rows'
      // horizontal inset is cancelled so they align with the surrounding content and the separators
      // span the full available width.
      divided: `
        divide-y divide-line-base-basic
        [&_[data-slot=item]]:px-0
      `,
      outline: `
        divide-y divide-line-base-basic overflow-hidden rounded-20 border border-line-base-basic bg-surface-base-normal
        shadow-bottom-05
      `,
    },
  },
  defaultVariants: { variant: 'default' },
})
